/**
 * QC-FINAL-CHECKLIST-001 — mandatory QC Final Review step shown before the existing
 * QC Approve modal (Title Search order-level QC, non External Portal).
 * Reuses FINAL_REVIEW_ITEMS / CHECKLIST_GROUPS / answered-required rules from the
 * processor checklist; shows the processor's answers read-only, collects independent
 * QC answers + acknowledgment, posts to /qc-final-review, then hands off to onReady().
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ClipboardCheck, Check, Loader2, AlertTriangle, UserRound, ShieldCheck, Equal } from 'lucide-react';
import { FINAL_REVIEW_ITEMS, CHECKLIST_GROUPS, ITEM_BY_KEY, OPT_SELECTED, emptyChecklist, isGAState, itemAnswered, isItemRequired } from '../search/FinalReviewModal';
import { getBusinessToday, futureDateClass, FutureDateError } from '../../utils/dateGuards';
import { formatDateTimeShortET } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtVal = (v) => { const t = (v || '').trim(); if (!t) return '—'; if (t === 'NA') return 'N/A'; return /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t.slice(5, 7)}/${t.slice(8, 10)}/${t.slice(0, 4)}` : t; };

export default function QCFinalReviewStep({ open, order, onCancel, onReady }) {
  const [meta, setMeta] = useState(null);
  const [checklist, setChecklist] = useState(emptyChecklist());
  const [ack, setAck] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const itemRefs = useRef({});

  useEffect(() => {
    if (!open || !order?.id) return;
    setMeta(null); setChecklist(emptyChecklist()); setAck(false); setAttempted(false);
    axios.get(`${API}/api/orders/${order.id}/qc-final-review`, { headers: authHeaders() })
      .then((r) => {
        setMeta(r.data);
        if (!r.data.applicable) onReady();                       // Tax/MLS/portal → straight to approve modal
        else if (r.data.qc_review_valid) onReady();              // already acknowledged against current fulfillment
      })
      .catch((e) => { toast.error(e.response?.data?.detail || 'Could not load the QC checklist'); onCancel(); });
  }, [open, order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isGA = isGAState(meta?.property_state);
  const ref = meta?.processor_review?.checklist || {};
  const required = useMemo(() => FINAL_REVIEW_ITEMS.filter((i) => isItemRequired(i, isGA)), [isGA]);
  const answered = required.filter((i) => itemAnswered(i, checklist[i.key], isGA) && (checklist[i.key] || '').trim()).length;
  const allAnswered = FINAL_REVIEW_ITEMS.every((i) => itemAnswered(i, checklist[i.key], isGA));
  const diffs = FINAL_REVIEW_ITEMS.filter((i) => (checklist[i.key] || '').trim() && (ref[i.key] || '').trim() && checklist[i.key].trim() !== ref[i.key].trim());

  const submit = async () => {
    const missing = FINAL_REVIEW_ITEMS.find((i) => isItemRequired(i, isGA) && !itemAnswered(i, checklist[i.key], isGA));
    if (missing || !ack) { setAttempted(true); itemRefs.current[missing?.key]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/orders/${order.id}/qc-final-review`, { checklist, checklist_acknowledged: true }, { headers: authHeaders() });
      onReady();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.errors || [d?.message || 'QC checklist could not be saved']).join('; '));
    } finally { setSaving(false); }
  };

  if (!open || !meta?.applicable || meta?.qc_review_valid) return null;

  const renderItem = (item) => {
    const val = checklist[item.key] || '';
    const req = isItemRequired(item, isGA);
    const ok = itemAnswered(item, val, isGA) && val.trim();
    const missing = req && !itemAnswered(item, val, isGA) && attempted;
    const refVal = (ref[item.key] || '').trim();
    const differs = refVal && val.trim() && refVal !== val.trim();
    return (
      <div key={item.key} ref={(el) => { itemRefs.current[item.key] = el; }}
        className={`grid grid-cols-[1fr_118px_auto] items-center gap-3 px-3 py-2 ${missing ? 'bg-amber-50 border-l-2 border-amber-400' : differs ? 'bg-rose-50/60' : 'bg-white'}`}
        data-testid={`qc-final-review-item-${item.key}`} data-differs={differs ? 'true' : 'false'}>
        <span className="text-[12.5px] font-medium text-slate-700 flex items-center gap-1.5 min-w-0">
          <span className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
            {ok ? <Check className="h-3 w-3" /> : FINAL_REVIEW_ITEMS.indexOf(item) + 1}
          </span>
          <span className="truncate">{item.label}</span>
          {item.gaOnly && !isGA && <span className="text-[10px] font-semibold text-slate-400 uppercase">(Optional)</span>}
          {missing && <span className="text-[9.5px] font-bold text-amber-600 uppercase">Required</span>}
        </span>
        <span className={`text-[11.5px] font-semibold rounded-md px-2 py-1 text-center ${differs ? 'bg-rose-100 text-rose-700' : refVal ? 'bg-slate-100 text-slate-600' : 'text-slate-300'}`}
          title="Search Processor's answer" data-testid={`qc-final-review-ref-${item.key}`}>
          {differs && <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" />}{fmtVal(refVal)}
        </span>
        {item.kind === 'date' ? (
          <div className="flex items-center gap-1.5">
            <div>
              <input type="date" max={getBusinessToday()} value={val === 'NA' ? '' : val} disabled={val === 'NA'}
                onChange={(e) => setChecklist((p) => ({ ...p, [item.key]: e.target.value }))}
                className={`h-8 text-[12px] border rounded-md px-2 focus:outline-none focus:border-blue-400 ${val === 'NA' ? 'opacity-40' : ''} ${missing ? 'border-amber-400' : 'border-slate-200'}${futureDateClass(val === 'NA' ? '' : val)}`}
                data-testid={`qc-final-review-date-${item.key}`} />
              <FutureDateError value={val === 'NA' ? '' : val} />
            </div>
            {item.allowNA && (
              <button type="button" onClick={() => setChecklist((p) => ({ ...p, [item.key]: p[item.key] === 'NA' ? '' : 'NA' }))}
                className={`px-3 py-1.5 text-[11.5px] font-semibold rounded-full border transition-colors ${val === 'NA' ? OPT_SELECTED.NA : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'}`}
                aria-pressed={val === 'NA'} data-testid={`qc-final-review-${item.key}-na`}>NA</button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {item.options.map((opt) => (
              <button key={opt} type="button" onClick={() => setChecklist((p) => ({ ...p, [item.key]: opt }))}
                className={`flex items-center gap-1 px-3 py-1.5 text-[11.5px] font-semibold rounded-full border transition-all duration-150 hover:-translate-y-px ${val === opt ? OPT_SELECTED[opt] : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-700'}`}
                aria-pressed={val === opt} data-testid={`qc-final-review-${item.key}-${opt.toLowerCase()}`}>
                {val === opt && <Check className="h-3 w-3" />}{opt}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" aria-describedby={undefined} data-testid="qc-final-review-modal">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-indigo-50 flex items-center justify-center"><ClipboardCheck className="h-4 w-4 text-indigo-600" /></span>
            QC Final Review — Mandatory Checklist
            <span className="ml-auto text-[11px] font-medium text-slate-400">Step 1 of 2</span>
          </DialogTitle>
        </DialogHeader>
        <div className="-mt-1 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[12px] font-semibold text-slate-600" data-testid="qc-final-review-progress">{answered} of {required.length} checks completed</p>
          <p className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
            <UserRound className="h-3.5 w-3.5 text-slate-400" />
            {meta.processor_review ? <>Processor answers by <b className="text-slate-700">{meta.processor_review.by}</b> · {formatDateTimeShortET(meta.processor_review.at)}</> : 'No processor checklist on file'}
          </p>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full transition-all duration-300 ${answered === required.length ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${required.length ? Math.round((answered / required.length) * 100) : 100}%` }} /></div>
        {meta.qc_review_stale && (
          <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="qc-final-review-stale">
            Fulfillment changed after your previous QC acknowledgment — please complete the checklist again.
          </div>
        )}
        <div className="grid grid-cols-[1fr_118px_auto] gap-3 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <span>Check</span><span className="text-center">Processor</span><span className="pr-2">QC answer</span>
        </div>
        <div className="flex-1 overflow-y-auto pr-1 space-y-3" data-testid="qc-final-review-checklist">
          {CHECKLIST_GROUPS.map((group) => {
            const items = group.keys.map((k) => ITEM_BY_KEY[k]).filter(Boolean);
            const GroupIcon = group.icon;
            return (
              <div key={group.title} className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                  <GroupIcon className="h-3.5 w-3.5 text-indigo-600" /><p className="text-[10.5px] font-bold text-slate-500 uppercase tracking-wider">{group.title}</p>
                </div>
                <div className="divide-y divide-slate-100">{items.map(renderItem)}</div>
              </div>
            );
          })}
        </div>
        <div className={`rounded-lg border px-3 py-2 text-[11.5px] flex items-center gap-2 ${diffs.length ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`} data-testid="qc-final-review-diff-summary">
          {diffs.length ? <><AlertTriangle className="h-3.5 w-3.5" /> {diffs.length} answer{diffs.length === 1 ? '' : 's'} differ from the processor — review before approving.</> : <><Equal className="h-3.5 w-3.5" /> No differences from the processor's answers so far.</>}
        </div>
        <label className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer ${!allAnswered ? 'opacity-60' : ''} ${attempted && !ack ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="qc-final-review-ack">
          <input type="checkbox" checked={ack} disabled={!allAnswered} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-600" data-testid="qc-final-review-ack-checkbox" />
          <span className="text-[12px] text-slate-700 leading-snug">
            <ShieldCheck className="inline h-3.5 w-3.5 text-indigo-600 mr-1 -mt-0.5" />I independently reviewed this order and completed the QC checklist accurately.
            {!allAnswered && <span className="block text-[11px] text-amber-700 mt-0.5">Answer all required checks above to enable this acknowledgment.</span>}
          </span>
        </label>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving} data-testid="qc-final-review-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="qc-final-review-continue">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Continue to Approve
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
